import type { Any } from '@livecontrol/core-utils';
// import { Str } from '@livecontrol/core-utils';
import { Scheduler } from '@livecontrol/scheduler/store';
import Axios from 'axios';
import { useCallback } from 'react';

export namespace Helper {
  export const fileToBlob = async (file: File): Promise<string> =>{
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (): void => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert file to blob'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  export const isVideoFile = (file: File): boolean=> {
    return file.type.startsWith('video/');
  }

  export const isImageFile = (file: File): boolean=> {
    return file.type.startsWith('image/');
  }

  export const isVideoUrl = async (url: string) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const contentType = response.headers.get('Content-Type');
      return contentType && contentType.startsWith('video/');
    } catch (error) {
      console.error("Error checking URL:", error);
      return false;
    }
  };

  export const useGetPresignedURL = (): ((
    name: string,
    type: string,
    path?: string | undefined
  ) => Promise<
    | {
        bucket: string;
        key: string;
        presignedURL: string;
      }
    | undefined
  >) => {
    const [mutatePutPresignedUrl] = Scheduler.Account.S3.useUpload();

    return useCallback(
      async (
        name: string,
        type: string,
        path?: string
      ): Promise<{ bucket: string; key: string; presignedURL: string } | undefined> => {
        const { bucket, key, url: presignedURL } = await mutatePutPresignedUrl(name, type, path);

        if (bucket && key && presignedURL) {
          return {
            bucket,
            key,
            presignedURL
          };
        }

        return undefined;
      },
      [mutatePutPresignedUrl]
    );
  };

  export const getS3URLFromBucket = (bucket: string, key: string): string =>
    `https://${bucket}.s3.amazonaws.com/${key}`;

  export const useUploadFile = (): ((config: {
    blob: Blob;
    name: string;
    type: string;
    path?: string | undefined;
  }) => Promise<string | undefined>) => {
    const getPresignedURL = useGetPresignedURL();

    return useCallback(
      async ({
        blob,
        name,
        type,
        path
      }: {
        blob: Blob;
        name: string;
        type: string;
        path?: string;
      }) => {
        const result = await getPresignedURL(name, type, path);

        if (!result) {
          return undefined;
        }

        const { bucket, key, presignedURL } = result;

        const options = {
          headers: {
            'Content-Type': type
          }
        };

        await Axios.put(presignedURL, blob, options);

        return getS3URLFromBucket(bucket, key);
      },
      [getPresignedURL]
    );
  };

  const createImage = async (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();

      image.addEventListener('load', () => {
        resolve(image);
      });

      image.addEventListener('error', (error) => {
        reject(error);
      });

      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  // canvas.toBlob only reliably encodes these MIME types; anything else falls back to JPEG.
  const CANVAS_ENCODABLE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

  export const getCroppedImg = async (
    imageSrc: string,
    crop: { x: number; y: number; width: number; height: number },
    mimeType = 'image/jpeg',
    quality = 0.65
  ): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    /*
     * setting canvas width & height allows us to
     * resize from the original image resolution
     */
    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx!.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const outputType = CANVAS_ENCODABLE_TYPES.has(mimeType) ? mimeType : 'image/jpeg';

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob: unknown) => {
          resolve(<Any>blob);
        },
        outputType,
        quality
      );
    });
  };

  export const getVideoThumbnail = (videoFile: File, timeInSeconds = 1): Promise<string> =>{
    return new Promise((resolve, reject) => {
      // Create a URL for the video
      const videoUrl = URL.createObjectURL(videoFile);
      
      // Create a video element
      const video = document.createElement('video');
      video.src = videoUrl;
      video.crossOrigin = '';
      video.currentTime = timeInSeconds;
      
      // When the video has loaded metadata
      video.onloadedmetadata = () => {
        // Seek to the specified time
        video.currentTime = Math.min(timeInSeconds, video.duration);
      };
      
      // When the video is seeked
      video.onseeked = () => {
        // Create a canvas
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Draw the video frame on the canvas
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Get the data URL
          const dataUrl = canvas.toDataURL('image/jpeg');
          
          // Clean up
          URL.revokeObjectURL(videoUrl);
          
          // Resolve with the data URL
          resolve(dataUrl);
        } else {
          reject(new Error('Could not get canvas context'));
        }
      };
      
      // Handle errors
      video.onerror = () => {
        URL.revokeObjectURL(videoUrl);
        reject(new Error('Error loading video'));
      };
    });
  }
}
